// Offline File Storage for YHS Operatives App
// Adapted cache storage implementation for photo uploads with offline support

export interface CachedFileData {
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  storedAt: string;
  temp_id: string;
  task_id?: string;
  job_id?: string;
  tag?: string;
}

export class FileCache {
  private static readonly UPLOADS_CACHE_NAME = 'yhs-uploads-v1';

  // Check if Cache Storage is available
  static isAvailable(): boolean {
    return 'caches' in window;
  }

  // Store file blob in Cache Storage (metadata handled by Redux)
  static async storeFile(file: File, fileId: string): Promise<void> {
    if (!this.isAvailable()) {
      throw new Error('Cache Storage not available');
    }

    try {
      console.log('💾 YHS Cache: Storing file blob:', fileId, 'Size:', file.size);

      // Check quota before storing
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        try {
          const estimate = await navigator.storage.estimate();
          const available = (estimate.quota || 0) - (estimate.usage || 0);
          console.log('💾 YHS Cache: Storage quota check - Available:', Math.round(available / 1024 / 1024), 'MB');

          if (available < file.size * 2) { // Need 2x file size for safety
            throw new Error(`Insufficient storage quota. Available: ${Math.round(available / 1024 / 1024)}MB, Needed: ${Math.round(file.size * 2 / 1024 / 1024)}MB`);
          }
        } catch (quotaError) {
          console.warn('⚠️ YHS Cache: Could not check storage quota:', quotaError);
        }
      }

      // Open uploads cache
      const cache = await caches.open(this.UPLOADS_CACHE_NAME);
      const fileKey = `${window.location.origin}/yhs-uploads/${fileId}`;

      // Create Response object from file with proper CORS headers
      const response = new Response(file, {
        status: 200,
        statusText: 'OK',
        headers: {
          'Content-Type': file.type || 'image/jpeg',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET',
          'Access-Control-Allow-Headers': 'Content-Type'
        }
      });

      // Store using full URL (this avoids network errors)
      await cache.put(fileKey, response);

      console.log('✅ YHS Cache: File blob stored successfully:', fileId);

    } catch (error) {
      console.error('❌ YHS Cache: Failed to store file:', fileId, error);

      // Provide more specific error information
      if (error instanceof Error) {
        if (error.message.includes('NetworkError') || error.message.includes('network error')) {
          console.error('❌ YHS Cache: This is likely a Cache Storage security or quota issue');
          console.error('❌ YHS Cache: Try clearing browser cache or check available storage');
        }
        if (error.message.includes('quota')) {
          console.error('❌ YHS Cache: Storage quota exceeded - need to clear space');
        }
      }

      throw error;
    }
  }

  // Retrieve file blob from Cache Storage (metadata comes from Redux)
  static async retrieveFileBlob(fileId: string): Promise<Blob | null> {
    if (!this.isAvailable()) {
      console.warn('❌ YHS Cache: Cache Storage not available');
      return null;
    }

    try {
      console.log('📥 YHS Cache: Retrieving file blob:', fileId);

      // Get file from cache
      const cache = await caches.open(this.UPLOADS_CACHE_NAME);
      const fileKey = `${window.location.origin}/yhs-uploads/${fileId}`;

      // Debug: List all cached keys to see what's actually in there
      const keys = await cache.keys();
      console.log('🔍 YHS Cache: Available keys in uploads cache:', keys.map(req => req.url));

      const response = await cache.match(fileKey);

      if (!response) {
        console.log('❌ YHS Cache: File blob not found:', fileId);
        console.log('🔍 YHS Cache: Looking for key:', fileKey);
        console.log('🔍 YHS Cache: Available keys count:', keys.length);
        return null;
      }

      // Check if response is valid
      if (!response.ok) {
        console.error('❌ YHS Cache: Response not OK:', response.status, response.statusText);
        return null;
      }

      // Return just the blob - metadata comes from Redux
      const blob = await response.blob();

      console.log('✅ YHS Cache: File blob retrieved successfully:', fileId, 'Size:', blob.size);
      return blob;

    } catch (error) {
      console.error('❌ YHS Cache: Failed to retrieve file blob:', fileId, error);
      console.error('❌ YHS Cache: Error details:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack
      });
      return null;
    }
  }

  // Helper to recreate File object from blob + Redux metadata
  static createFileFromBlob(blob: Blob, metadata: { name: string; type: string; lastModified: number }): File {
    return new File([blob], metadata.name, {
      type: metadata.type,
      lastModified: metadata.lastModified
    });
  }

  // Remove file blob from Cache Storage (Redux handles metadata removal)
  static async removeFile(fileId: string): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      console.log('🗑️ YHS Cache: Removing file blob:', fileId);

      // Remove only the blob from uploads cache
      const cache = await caches.open(this.UPLOADS_CACHE_NAME);
      const fileKey = `${window.location.origin}/yhs-uploads/${fileId}`;
      const deleted = await cache.delete(fileKey);

      console.log('✅ YHS Cache: File blob removed:', fileId, 'Deleted:', deleted);
      return deleted;

    } catch (error) {
      console.error('❌ YHS Cache: Failed to remove file blob:', fileId, error);
      return false;
    }
  }

  // Clear all cached file blobs (Redux handles metadata clearing)
  static async clearAllFiles(): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      console.log('🧹 YHS Cache: Clearing all file blobs');

      // Delete only the uploads cache
      const uploadsDeleted = await caches.delete(this.UPLOADS_CACHE_NAME);

      console.log('✅ YHS Cache: All file blobs cleared:', uploadsDeleted);
      return uploadsDeleted;

    } catch (error) {
      console.error('❌ YHS Cache: Failed to clear all file blobs:', error);
      return false;
    }
  }

  // Get cache storage usage info
  static async getStorageInfo(): Promise<{ usedBytes: number; fileCount: number }> {
    if (!this.isAvailable()) {
      return { usedBytes: 0, fileCount: 0 };
    }

    try {
      const cache = await caches.open(this.UPLOADS_CACHE_NAME);
      const keys = await cache.keys();

      let usedBytes = 0;
      let fileCount = 0;

      // Calculate size from cache entries
      for (const request of keys) {
        const response = await cache.match(request);
        if (response && request.url.includes('/yhs-uploads/')) {
          const blob = await response.blob();
          usedBytes += blob.size;
          fileCount++;
        }
      }

      return { usedBytes, fileCount };

    } catch (error) {
      console.error('❌ YHS Cache: Failed to get storage info:', error);
      return { usedBytes: 0, fileCount: 0 };
    }
  }

  // Convert data URL to File object (for camera photos)
  static async dataURLtoFile(dataURL: string, filename: string): Promise<File> {
    const response = await fetch(dataURL);
    const blob = await response.blob();
    return new File([blob], filename, { type: 'image/jpeg', lastModified: Date.now() });
  }

  // Helper to check if a photo is cached (by temp_id)
  static async isPhotoCached(tempId: string): Promise<boolean> {
    try {
      const blob = await this.retrieveFileBlob(tempId);
      return blob !== null;
    } catch {
      return false;
    }
  }
}